export const UH_CONSENT_COOKIE = 'uh_consent';
export const UH_CONSENT_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

export type StoredConsentChoice = 'accept' | 'reject' | 'custom';

export type StoredConsentCategories = {
  stats: boolean;
  analytics: boolean;
  ads: boolean;
};

type StoredMode = 'NOTICE' | 'OUT' | 'IN';

/** Modes in force when a custom choice was saved. An opt-out default that was left on is not consent. */
export type StoredConsentModes = {
  stats: StoredMode;
  analytics: StoredMode;
  ads: StoredMode;
};

const MODES: readonly string[] = ['NOTICE', 'OUT', 'IN'];

export type ConsentRecord = {
  v: 1;
  ts: number;
  store: string;
  choice: StoredConsentChoice;
  categories?: StoredConsentCategories;
  /** Required with categories. */
  modes?: StoredConsentModes;
  /** Global Privacy Control was set when this choice was saved. */
  gpc?: boolean;
};

export function serializeConsent(record: ConsentRecord): string {
  return JSON.stringify(record);
}

export function parseConsent(raw: string | null | undefined): ConsentRecord | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<ConsentRecord>;
    if (value.v !== 1) return null;
    if (
      value.choice !== 'accept' &&
      value.choice !== 'reject' &&
      value.choice !== 'custom'
    ) {
      return null;
    }
    if (typeof value.ts !== 'number' || typeof value.store !== 'string') {
      return null;
    }
    if (value.choice === 'custom') {
      const categories = value.categories;
      if (
        !categories ||
        typeof categories.stats !== 'boolean' ||
        typeof categories.analytics !== 'boolean' ||
        typeof categories.ads !== 'boolean'
      ) {
        return null;
      }
      // Custom records saved before modes were stored are asked again.
      const modes = value.modes;
      if (
        !modes ||
        !MODES.includes(modes.stats) ||
        !MODES.includes(modes.analytics) ||
        !MODES.includes(modes.ads)
      ) {
        return null;
      }
      return {
        v: 1,
        ts: value.ts,
        store: value.store,
        choice: 'custom',
        categories: {
          stats: categories.stats,
          analytics: categories.analytics,
          ads: categories.ads,
        },
        modes: { stats: modes.stats, analytics: modes.analytics, ads: modes.ads },
        ...(value.gpc === true ? { gpc: true } : {}),
      };
    }
    return {
      v: 1,
      ts: value.ts,
      store: value.store,
      choice: value.choice,
      ...(value.gpc === true ? { gpc: true } : {}),
    };
  } catch {
    return null;
  }
}
